import React, { useEffect, useRef } from "react";
import { Menu, X } from "lucide-react";

// Slide-in side panel shared by the mobile menu and the desktop services menu. It stays mounted so it
// animates both ways, slides from the start edge (right in Arabic), closes on overlay click or Escape,
// locks page scroll, moves focus into the panel and back to the trigger, and is inert while closed.
export default function SideDrawer({ open, onClose, labelledBy, className = "", zIndex = "z-40", children }) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = "hidden";
    // Prefer the element marked data-autofocus (the close button), else the first control.
    const panel = panelRef.current;
    (panel?.querySelector("[data-autofocus]") || panel?.querySelector("button, a[href]"))?.focus({
      preventScroll: true,
    });

    const handleEscape = (event) => {
      if (event.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus({ preventScroll: true });
    };
  }, [open]);

  return (
    // Visibility flips only after the 300ms slide-out, so nothing (not even the panel's shadow) lingers once closed.
    <div
      className={`fixed inset-0 transition-[visibility] duration-300 ${zIndex} ${
        open ? "visible" : "pointer-events-none invisible"
      } ${className}`}
      inert={!open}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-slate-900/40 transition-opacity duration-300 ease-out motion-reduce:transition-none ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => onCloseRef.current?.()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`absolute top-0 start-0 flex h-dvh w-72 max-w-[85vw] flex-col bg-white shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none dark:bg-slate-800 dark:text-white ${
          open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        {children}
      </div>
    </div>
  );
}

// Hamburger that morphs into an X while its menu is open.
export function DrawerToggleButton({ open, onClick, label, controls, className = "", ...props }) {
  const iconClass = "absolute transition-all duration-300 motion-reduce:transition-none";
  return (
    <button
      {...props}
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-expanded={open}
      aria-controls={controls}
      className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-600 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white ${
        open ? "bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-white" : ""
      } ${className}`}
    >
      <Menu
        size={20}
        strokeWidth={2}
        aria-hidden="true"
        className={`${iconClass} ${open ? "rotate-90 scale-75 opacity-0" : "rotate-0 scale-100 opacity-100"}`}
      />
      <X
        size={20}
        strokeWidth={2}
        aria-hidden="true"
        className={`${iconClass} ${open ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-75 opacity-0"}`}
      />
    </button>
  );
}
